/* eslint-disable @typescript-eslint/no-explicit-any */
import { get, post } from '@/apis/client';

import type { PageData } from '@/app/types';
import { ContentTypeCountEntry, ContentTypeMap } from '@/app/types';

// Standardized Base Endpoint Constant to eliminate endpoint mismatch
const ENDPOINTS = {
  CONTENT_TYPES: 'content-types',
  CONTENTS: 'contents',
};

export const getContentTypesList = async (): Promise<ContentTypeMap> => {
  try {
    const data = await get(`${ENDPOINTS.CONTENT_TYPES}`);
    if (!data || data.error === true) {
      throw new Error(`No content types found`);
    }
    return data.results || {};
  } catch (error) {
    console.error(`Failed to GET content types from /${ENDPOINTS.CONTENT_TYPES}`, error);
    throw error;
  }
};

export const getContentTypeCountsByTag = async (tag: string): Promise<ContentTypeCountEntry[]> => {
  try {
    const data = await get(`${ENDPOINTS.CONTENT_TYPES}/?tag=${encodeURIComponent(tag)}`);
    if (!data || data.error === true) {
      throw new Error(`No content type counts found for tag: ${tag}`);
    }
    return data.results || [];
  } catch (error) {
    console.error(`Failed to GET content type counts for tag: ${tag}`, error);
    throw error;
  }
};

/**
 * Fetch a single content item by slug.
 */
export const getContentBySlug = async <T = any>(
  contentType: string,
  slug: string
): Promise<T | null> => {
  if (!contentType || !slug) return null;

  const endpoint = `${ENDPOINTS.CONTENTS}/${contentType}/${slug}`;

  try {
    const result = await get(endpoint);

    // Explicit 404 handling from API client layer
    if (result === null || result === undefined) {
      return null;
    }

    const data =
      result && typeof result === 'object' && 'data' in result ? (result as any).data : result;

    if (!data || typeof data !== 'object') {
      throw new Error('Empty or invalid API response');
    }

    if ('error' in data && data.error === true) {
      return null;
    }

    return data as T;
  } catch (err) {
    console.error(`[API] Error fetching ${contentType} slug "${slug}":`, err);
    // Rethrowing allows server context to handle transient failures vs hard 404s
    throw err;
  }
};

export const getContentTypesDetail = async (contentType: string) => {
  try {
    const data = await get(`${ENDPOINTS.CONTENT_TYPES}/${contentType}`);
    if (!data || data.error === true) {
      return null;
    }
    return data;
  } catch (error) {
    console.error(`Failed to GET content ${contentType} detail`, error);
    throw error;
  }
};

export async function getContentTypesListArray(): Promise<string[]> {
  try {
    const apiResponse = await get(`${ENDPOINTS.CONTENT_TYPES}`);
    if (apiResponse && apiResponse.error === false && apiResponse.results) {
      return Object.keys(apiResponse.results);
    }
    return [];
  } catch (error) {
    console.error('getContentTypesListArray', `Failed to GET content types`, error);
    return []; // Return empty array to avoid breaking caller during route initialization
  }
}

export const getContentTypeListOfContent = async <T = any>(
  contentType: string,
  payload?: { tag?: string; page?: number | string; size?: number | string; [key: string]: any }
): Promise<T | null> => {
  const page = payload?.page ?? 1;
  const size = payload?.size ?? 12;

  const queryParams = new URLSearchParams();
  if (payload?.tag) queryParams.append('tag', payload.tag);
  if (payload?.sort) queryParams.append('sort', payload.sort);
  queryParams.append('page', String(page));
  queryParams.append('size', String(size));

  const endpoint = `${ENDPOINTS.CONTENT_TYPES}/${contentType}/list?${queryParams.toString()}`;

  try {
    const result = await get(endpoint);
    if (!result) return null;
    return result as T;
  } catch (err) {
    console.error(`[API] Error while fetching list for ${contentType}:`, err);
    throw err;
  }
};

export const postContentTypeListOfContent = async <T = any>(
  contentType: string,
  payload?: { tag?: string; page?: number | string; size?: number | string; [key: string]: any }
): Promise<T | null> => {
  const page = payload?.page ?? 1;
  const size = payload?.size ?? 12;

  const queryParams = new URLSearchParams();
  if (payload?.tag) queryParams.append('tag', payload.tag);
  if (payload?.sort) queryParams.append('sort', payload.sort);
  queryParams.append('page', String(page));
  queryParams.append('size', String(size));

  const endpoint = `${ENDPOINTS.CONTENT_TYPES}/${contentType}/list?${queryParams.toString()}`;
  const body = { ...payload, page, size };

  try {
    const result = await post(endpoint, body);
    const data =
      result && typeof result === 'object' && 'data' in result ? (result as any).data : result;

    if (!data || typeof data !== 'object') {
      throw new Error('Empty or invalid API response');
    }

    return data as T;
  } catch (err) {
    console.error(`[API] Error while posting list for ${contentType}:`, err);
    throw err;
  }
};

export async function getContentData(
  contentType: string,
  slug: string
): Promise<PageData | undefined> {
  try {
    type ContentResponse = { results?: PageData[] } | PageData;
    const data = await getContentBySlug<ContentResponse>(contentType, slug);

    if (!data) {
      return undefined;
    }

    if ('results' in data && Array.isArray(data.results)) {
      return data.results[0] ?? undefined;
    }

    return data as PageData;
  } catch (error) {
    console.error(`[getContentData] Failed to retrieve data for ${contentType}/${slug}:`, error);
    return undefined;
  }
}
